import { useState, useRef, useCallback, useEffect } from 'react';
import SoftMeter, { parseMeterConfig, parseWaitConfig } from '../services/softMeter';
import { matchTraceWindow, routeBetween, isOsrmEnabled } from '../services/osrmMatch';

const METER_SEND_INTERVAL_MS = 30_000; // throttle METER messages to every 30s
const TRACE_FLUSH_INTERVAL_MS = 60_000; // OSRM Match flush cadence
const TRACE_FLUSH_AT_POINTS = 100; // OSRM Match flush threshold by buffered points

/** Local haversine — used by the visibilitychange handler to size the gap window so that the
 *  matched OSRM Route distance REPLACES the gap segment instead of adding on top of it. */
function haversineMetersLocal(a, b) {
  if (!a || !b) return 0;
  const R = 6_371_000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  const h = s1 * s1 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * s2 * s2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * React hook wrapping the SoftMeter service.
 * @param {object} [options]
 * @param {boolean} [options.isMetric=false] When false, distances/speeds use miles and mph.
 * @param {function} [options.onMeterTick] Throttled callback (every 30 s) for METER socket messages.
 */
export default function useSoftMeter(options = {}) {
  const { isMetric = false, onMeterTick } = options;

  const [fare, setFare] = useState(0);
  const [distance, setDistance] = useState(0);
  const [fareMode, setFareMode] = useState('distance');
  const [isRunning, setIsRunning] = useState(false);
  const [meterConfig, setMeterConfig] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [fareTimeSeconds, setFareTimeSeconds] = useState(0);
  const [waitTime, setWaitTime] = useState(0);
  const [waitCharge, setWaitCharge] = useState(0);
  const [distanceCharge, setDistanceCharge] = useState(0);
  const [timeCharge, setTimeCharge] = useState(0);

  const meterRef = useRef(null);
  const onMeterTickRef = useRef(onMeterTick);
  onMeterTickRef.current = onMeterTick;
  const lastMeterSendRef = useRef(0);
  const displayTimerRef = useRef(null);
  /** Cursor into the trace buffer — points before this index have already been OSRM-matched. */
  const traceFlushedToTsRef = useRef(0);
  const flushTimerRef = useRef(null);
  /** Block re-entrant flushes when fetch is in flight. */
  const flushInFlightRef = useRef(false);

  const handleFareUpdate = useCallback((newFare, mode, dist, waitChargeDollars = 0) => {
    setFare(newFare);
    setFareMode(mode);
    setDistance(dist);
    const wc = Number(waitChargeDollars);
    setWaitCharge(Number.isFinite(wc) ? Math.round(wc * 100) / 100 : 0);
  }, []);

  const startDisplayTimer = useCallback(() => {
    if (displayTimerRef.current) clearInterval(displayTimerRef.current);
    displayTimerRef.current = setInterval(() => {
      if (meterRef.current && meterRef.current.enabled) {
        setElapsedTime(meterRef.current.elapsedSeconds);
        setFareTimeSeconds(meterRef.current.fareTimeSeconds);
        setWaitTime(meterRef.current.waitTimeSeconds);
        setWaitCharge(Math.round((meterRef.current.waitCharge || 0) * 100) / 100);
        setDistanceCharge(Math.round((meterRef.current.distanceCharge || 0) * 100) / 100);
        setTimeCharge(Math.round((meterRef.current.timeCharge || 0) * 100) / 100);
      }
    }, 1000);
  }, []);

  const stopDisplayTimer = useCallback(() => {
    if (displayTimerRef.current) {
      clearInterval(displayTimerRef.current);
      displayTimerRef.current = null;
    }
  }, []);

  /** Pull all buffered points after the last flushed timestamp, send to OSRM Match. */
  const flushTrace = useCallback(async () => {
    const m = meterRef.current;
    if (!m || !m.enabled) return;
    if (!isOsrmEnabled()) return;
    if (flushInFlightRef.current) return;

    const buffer = m.getTraceBuffer();
    const window = buffer.filter((p) => p.ts > traceFlushedToTsRef.current);
    if (window.length < 2) return;

    flushInFlightRef.current = true;
    try {
      const result = await matchTraceWindow(window);
      if (result && Number.isFinite(result.matchedDistanceMeters) && result.matchedDistanceMeters >= 0) {
        // Estimate the raw window distance from buffered points so we can replace it.
        let raw = 0;
        for (let i = 1; i < window.length; i++) {
          const a = window[i - 1];
          const b = window[i];
          const dLat = (b.lat - a.lat) * (Math.PI / 180);
          const dLng = (b.lng - a.lng) * (Math.PI / 180);
          const sLat = Math.sin(dLat / 2);
          const sLng = Math.sin(dLng / 2);
          const h =
            sLat * sLat +
            Math.cos((a.lat * Math.PI) / 180) *
              Math.cos((b.lat * Math.PI) / 180) *
              sLng * sLng;
          raw += 2 * 6_371_000 * Math.asin(Math.sqrt(h));
        }
        m.applyMatchedDistance(result.matchedDistanceMeters, raw);
        traceFlushedToTsRef.current = window[window.length - 1].ts;
      }
    } catch (err) {
      console.warn('SoftMeter: OSRM Match flush failed (raw odometer authoritative)', err?.message || err);
    } finally {
      flushInFlightRef.current = false;
    }
  }, []);

  const startFlushLoop = useCallback(() => {
    if (flushTimerRef.current) clearInterval(flushTimerRef.current);
    if (!isOsrmEnabled()) return;
    flushTimerRef.current = setInterval(() => {
      const m = meterRef.current;
      if (!m || !m.enabled) return;
      const buf = m.getTraceBuffer();
      if (buf.length >= TRACE_FLUSH_AT_POINTS) {
        flushTrace();
      } else {
        flushTrace();
      }
    }, TRACE_FLUSH_INTERVAL_MS);
  }, [flushTrace]);

  const stopFlushLoop = useCallback(() => {
    if (flushTimerRef.current) {
      clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  }, []);

  /**
   * Run OSRM gap-recovery against a captured pre-gap anchor.
   *
   * Polls the meter's trace buffer for a fix newer than the anchor (up to 30 s — iOS
   * GPS chip warm-up can be slow), then issues an OSRM Route between the two points
   * and credits the result via `applyGapInterval` (drive-vs-wait split). On any
   * failure or implausible distance, falls back to straight-line haversine.
   *
   * Used by BOTH the visibilitychange→visible handler (warm resume) and the
   * `resumeFromPersisted` cold-restart path. Anchor is consumed before this is
   * called; caller is responsible for ensuring `_gapPending=true` will be cleared
   * by the first post-resume fix landing in the trace buffer.
   */
  const runGapRecovery = useCallback((anchor, opts = {}) => {
    const tag = opts.coldStart ? '[COLDSTART]' : '[METER]';
    const initialMeter = meterRef.current;
    if (!initialMeter || !anchor || !isOsrmEnabled()) {
      // eslint-disable-next-line no-console
      console.log(`${tag} runGapRecovery skipped`, {
        hasMeter: !!initialMeter, hasAnchor: !!anchor, osrmEnabled: isOsrmEnabled(),
      });
      return;
    }
    if (opts.coldStart) {
      // eslint-disable-next-line no-console
      console.log('[COLDSTART] runGapRecovery armed — waiting for post-restart GPS fix', {
        anchor,
        anchorAgeMs: Date.now() - anchor.ts,
        anchorAgeSec: Math.round((Date.now() - anchor.ts) / 1000),
        traceBufferLen: initialMeter.getTraceBuffer().length,
      });
    }
    const start = Date.now();
    const waitFor = 30000;
    let pollCount = 0;
    const tryRecover = async () => {
      pollCount += 1;
      // CRITICAL: always read meterRef.current fresh inside the poll. StrictMode and
      // hot-reload can swap the meter instance between when this closure was armed
      // and when it polls — capturing `m` at arm-time would forever poll an orphan
      // meter's frozen trace buffer while live GPS fixes flow to a new instance.
      const m = meterRef.current;
      if (!m) return false;
      const buf = m.getTraceBuffer();
      const after = buf.find((p) => p.ts > anchor.ts);
      if (!after) {
        if (opts.coldStart && pollCount % 4 === 1) {
          // eslint-disable-next-line no-console
          console.log('[COLDSTART] no post-anchor fix yet — still polling', {
            pollCount, elapsedMs: Date.now() - start, traceBufferLen: buf.length,
            latestTraceTs: buf.length ? buf[buf.length - 1].ts : null,
            anchorTs: anchor.ts,
            meterIdentitySwapped: m !== initialMeter,
            meterJob: m.jobNumber,
            meterGapPending: m._gapPending,
            meterLastFixTs: m._lastFixTimestamp,
          });
        }
        return false;
      }
      // The gap segment was SKIPPED entirely by onLocationUpdate (post-gap branch returns
      // before any distance is accrued). So the matched road distance must be ADDED in
      // full — not delta-corrected. windowMeters (haversine) is the additive fallback
      // when OSRM is unavailable. The gap DURATION is also credited (wait or time-mode
      // fare) via applyGapInterval — without that, idle background billed $0 of wait.
      const windowMeters = haversineMetersLocal(anchor, after);
      const gapDurationMs = Math.max(0, after.ts - anchor.ts);
      if (opts.coldStart) {
        // eslint-disable-next-line no-console
        console.log('[COLDSTART] post-restart GPS fix arrived — calling OSRM Route', {
          anchor: { lat: anchor.lat, lng: anchor.lng, ts: anchor.ts },
          after: { lat: after.lat, lng: after.lng, ts: after.ts },
          haversineMeters: Math.round(windowMeters),
          gapDurationMs,
          gapDurationSec: Math.round(gapDurationMs / 1000),
          pollCount,
          totalWaitMs: Date.now() - start,
        });
      }
      // Advance the flushTrace cursor PAST the post-gap fix so the next periodic flush
      // doesn't re-process the gap span (its raw window would include the gap haversine
      // and produce an over-subtraction delta).
      traceFlushedToTsRef.current = after.ts;
      try {
        const route = await routeBetween(
          { lat: anchor.lat, lng: anchor.lng },
          { lat: after.lat, lng: after.lng }
        );
        let matched = route && Number.isFinite(route.distanceMeters) ? route.distanceMeters : NaN;
        const expectedDurationSec =
          route && Number.isFinite(route.durationSeconds) ? route.durationSeconds : 0;
        if (opts.coldStart) {
          // eslint-disable-next-line no-console
          console.log('[COLDSTART] OSRM Route response', {
            ok: !!route,
            osrmDistanceMeters: Number.isFinite(matched) ? Math.round(matched) : null,
            osrmDurationSec: Math.round(expectedDurationSec),
            haversineMeters: Math.round(windowMeters),
            gapDurationSec: Math.round(gapDurationMs / 1000),
          });
        }
        // Sanity cap: reject obviously bogus routes (> 50 km between adjacent fixes) and
        // fall back to the straight-line haversine — keeps OSRM outliers from wrecking the meter.
        if (Number.isFinite(matched) && matched >= 0 && matched <= 50_000) {
          m.applyGapInterval(matched, gapDurationMs, expectedDurationSec);
        } else {
          if (Number.isFinite(matched)) {
            console.warn('SoftMeter: OSRM Route returned implausible distance, using haversine',
              { matchedM: matched });
          }
          // No matched distance available — fallback path uses implied-speed heuristic
          // (no expected duration, so applyGapInterval treats based on speed).
          m.applyGapInterval(windowMeters, gapDurationMs);
        }
      } catch (err) {
        if (opts.coldStart) {
          // eslint-disable-next-line no-console
          console.warn('[COLDSTART] OSRM Route failed — falling back to haversine', {
            error: err?.message || String(err),
            haversineMeters: Math.round(windowMeters),
            gapDurationSec: Math.round(gapDurationMs / 1000),
          });
        } else {
          console.warn('SoftMeter: OSRM Route gap recovery failed', err?.message || err);
        }
        // Fallback: at least credit the straight-line distance + duration.
        m.applyGapInterval(windowMeters, gapDurationMs);
      }
      return true;
    };
    (async () => {
      if (!(await tryRecover())) {
        const poll = setInterval(async () => {
          if (Date.now() - start > waitFor) {
            clearInterval(poll);
            if (opts.coldStart) {
              // eslint-disable-next-line no-console
              console.warn('[COLDSTART] gap recovery TIMED OUT — no post-restart GPS fix within 30s', {
                pollCount, anchor,
              });
            }
            return;
          }
          if (await tryRecover()) clearInterval(poll);
        }, 500);
      }
    })();
  }, []);

  /**
   * Build a SoftMeter wired with the standard hook callbacks. Used by both fresh-start
   * and persisted-state restore paths so they don't drift.
   */
  const constructMeter = useCallback((jobNumber, config, waitConfig, isMetricFlag) => {
    return new SoftMeter(jobNumber, config, {
      isMetric: isMetricFlag,
      waitConfig,
      onFareUpdate: handleFareUpdate,
      onMeterTick: (stats) => {
        const now = Date.now();
        if (now - lastMeterSendRef.current >= METER_SEND_INTERVAL_MS) {
          lastMeterSendRef.current = now;
          if (onMeterTickRef.current) onMeterTickRef.current(stats);
        }
      },
    });
  }, [handleFareUpdate]);

  /**
   * Resume a meter from a previously-persisted snapshot (created in this or a prior
   * session). Returns true when state was actually restored.
   */
  const resumeFromPersisted = useCallback((saved) => {
    if (!saved || !saved.config) return false;
    const meter = constructMeter(
      saved.jobNumber,
      saved.config,
      saved.waitConfig || null,
      saved.isMetric ?? isMetric
    );
    meter.restoreFrom(saved);
    meterRef.current = meter;
    meter._enabled = true;
    meter._broadcastFare = true;
    meter._startTimer();
    meter.calculateFare(false);
    meter._persistState();

    setMeterConfig(saved.config);
    setIsRunning(true);
    setFare(saved.fare || 0);
    setDistance(meter.totalDistance);
    setElapsedTime(meter.elapsedSeconds);
    setFareTimeSeconds(meter.fareTimeSeconds);
    setWaitTime(meter.waitTimeSeconds);
    setWaitCharge(Math.round((meter.waitCharge || 0) * 100) / 100);
    setDistanceCharge(Math.round((meter.distanceCharge || 0) * 100) / 100);
    setTimeCharge(Math.round((meter.timeCharge || 0) * 100) / 100);
    lastMeterSendRef.current = Date.now();
    startDisplayTimer();
    startFlushLoop();

    if (isOsrmEnabled()) {
      flushTrace();
    }

    // Cold-restart gap recovery: if the snapshot was taken mid-background (onPause set
    // _gapPending + _lastValidPointBeforeGap before persisting), the visibilitychange
    // handler will NEVER fire after a fresh process start (the page mounts directly into
    // 'visible'). Kick off the same OSRM Route recovery here so the missed road distance
    // and offline wait/time interval are credited once a post-restart fix arrives.
    // eslint-disable-next-line no-console
    console.log('[COLDSTART] resumeFromPersisted — checking for pending gap', {
      gapPending: meter._gapPending,
      hasAnchor: !!meter._lastValidPointBeforeGap,
      anchor: meter._lastValidPointBeforeGap,
      lastFixTs: meter._lastFixTimestamp,
      lastFixAgeSec: meter._lastFixTimestamp
        ? Math.round((Date.now() - meter._lastFixTimestamp) / 1000)
        : null,
      osrmEnabled: isOsrmEnabled(),
    });
    if (meter._gapPending && meter._lastValidPointBeforeGap && isOsrmEnabled()) {
      const anchor = meter.consumeGapAnchor();
      if (anchor) {
        // eslint-disable-next-line no-console
        console.log('[COLDSTART] cold-start detected — triggering OSRM gap recovery', {
          anchor,
          anchorAgeSec: Math.round((Date.now() - anchor.ts) / 1000),
        });
        runGapRecovery(anchor, { coldStart: true });
      }
    } else if (meter._lastValidPointBeforeGap && !meter._gapPending) {
      // eslint-disable-next-line no-console
      console.warn('[COLDSTART] anchor present but gapPending=false — gap state was incomplete', {
        anchor: meter._lastValidPointBeforeGap,
      });
    }

    // eslint-disable-next-line no-console
    console.log('[METER] useSoftMeter RESUMED meter from persisted state', {
      jobNumber: meter.jobNumber,
      totalDistance: meter.totalDistance,
      fareTimeSec: meter.fareTimeSeconds,
      waitSec: meter.waitTimeSeconds,
      fare: saved.fare,
    });
    return true;
  }, [constructMeter, isMetric, startDisplayTimer, startFlushLoop, flushTrace, runGapRecovery]);

  const startMeter = useCallback((jobNumber, rawMeterConfig, rawWaitConfig) => {
    const trimmedJob = String(jobNumber ?? '').trim();
    const current = meterRef.current;
    const currentJob = current ? String(current.jobNumber ?? '').trim() : '';

    // eslint-disable-next-line no-console
    console.log('[METER] useSoftMeter.startMeter called', {
      jobNumber: trimmedJob,
      hadPriorMeter: !!current,
      priorJob: currentJob,
      priorEnabled: current?.enabled,
    });

    // Same job already running in-memory — no-op so STARTM replay never resets totals.
    if (current && current.enabled && currentJob !== '' && currentJob === trimmedJob) {
      // eslint-disable-next-line no-console
      console.log('[METER] startMeter NO-OP — meter already running for this job');
      return;
    }

    // No live meter, but persisted state matches this job (e.g. STARTM after reload):
    // resume the saved progress instead of creating a fresh meter.
    if (!current && SoftMeter.hasPersistedStateFor(trimmedJob)) {
      const saved = SoftMeter.getPersistedState();
      if (resumeFromPersisted(saved)) return;
    }

    // Different job (or no persistence match) — prior meter belongs to a finished trip,
    // so disable() is correct here (also clears its persisted state).
    if (current) {
      current.disable();
    }

    const config = parseMeterConfig(rawMeterConfig);
    if (!config) {
      console.warn('SoftMeter: invalid meter config, not starting');
      return;
    }

    const waitConfig = parseWaitConfig(rawWaitConfig);
    setMeterConfig(config);

    const meter = constructMeter(trimmedJob || jobNumber, config, waitConfig, isMetric);

    meterRef.current = meter;
    traceFlushedToTsRef.current = 0;
    meter.enable();
    lastMeterSendRef.current = Date.now();

    setIsRunning(true);
    setFare(meter.fare);
    setFareMode(meter.fareMode);
    setDistance(meter.totalDistance);
    setElapsedTime(meter.elapsedSeconds);
    setFareTimeSeconds(meter.fareTimeSeconds);
    setWaitTime(meter.waitTimeSeconds);
    setWaitCharge(Math.round((meter.waitCharge || 0) * 100) / 100);
    setDistanceCharge(Math.round((meter.distanceCharge || 0) * 100) / 100);
    setTimeCharge(Math.round((meter.timeCharge || 0) * 100) / 100);
    startDisplayTimer();
    startFlushLoop();
  }, [isMetric, constructMeter, resumeFromPersisted, startDisplayTimer, startFlushLoop]);

  /**
   * Stop the meter. When `expectedJobNumber` is provided, no-op + warn if the running
   * meter belongs to a different trip — protects against cross-trip stop races.
   */
  const stopMeter = useCallback((expectedJobNumber) => {
    const m = meterRef.current;
    // eslint-disable-next-line no-console
    console.log('[METER] useSoftMeter.stopMeter called', {
      expectedJobNumber,
      hasMeter: !!m,
      meterJobNumber: m?.jobNumber,
      stack: new Error().stack?.split('\n').slice(1, 6).join(' | '),
    });
    if (!m) {
      setIsRunning(false);
      setFareTimeSeconds(0);
      setWaitCharge(0);
      setDistanceCharge(0);
      setTimeCharge(0);
      stopDisplayTimer();
      stopFlushLoop();
      return;
    }
    if (
      expectedJobNumber !== undefined &&
      expectedJobNumber !== null &&
      String(expectedJobNumber).trim() !== '' &&
      String(m.jobNumber).trim() !== String(expectedJobNumber).trim()
    ) {
      console.warn(
        'SoftMeter.stopMeter: expected trip',
        expectedJobNumber,
        'but meter is running for',
        m.jobNumber,
        '— ignoring'
      );
      return;
    }
    m.disable();
    // Drop the reference so the unmount cleanup's detachInMemory() can't
    // re-persist a snapshot of the disabled meter and trigger an auto-resume
    // on the next mount (which would re-enable the meter for a completed trip).
    meterRef.current = null;
    setIsRunning(false);
    setFareTimeSeconds(0);
    setWaitCharge(0);
    setDistanceCharge(0);
    setTimeCharge(0);
    stopDisplayTimer();
    stopFlushLoop();
  }, [stopDisplayTimer, stopFlushLoop]);

  /**
   * Pause the meter without destroying it. Used before showing the fare
   * confirmation modal so that hitting Cancel can re-enable the same meter
   * (stopMeter nulls meterRef.current, which would make reEnableMeter a no-op).
   * The meter remains persisted, so a reload mid-modal still resumes correctly.
   */
  const pauseMeter = useCallback((expectedJobNumber) => {
    const m = meterRef.current;
    // eslint-disable-next-line no-console
    console.log('[METER] useSoftMeter.pauseMeter called', {
      expectedJobNumber,
      hasMeter: !!m,
      meterJobNumber: m?.jobNumber,
    });
    if (!m) {
      setIsRunning(false);
      stopDisplayTimer();
      stopFlushLoop();
      return;
    }
    if (
      expectedJobNumber !== undefined &&
      expectedJobNumber !== null &&
      String(expectedJobNumber).trim() !== '' &&
      String(m.jobNumber).trim() !== String(expectedJobNumber).trim()
    ) {
      console.warn(
        'SoftMeter.pauseMeter: expected trip',
        expectedJobNumber,
        'but meter is running for',
        m.jobNumber,
        '— ignoring'
      );
      return;
    }
    m.disable();
    // NOTE: do NOT null meterRef.current — reEnableMeter needs it.
    setIsRunning(false);
    stopDisplayTimer();
    stopFlushLoop();
  }, [stopDisplayTimer, stopFlushLoop]);

  /**
   * Re-enable the existing meter (e.g. user cancelled the fare confirmation modal).
   * When `expectedJobNumber` is provided, no-op + warn on mismatch.
   */
  const reEnableMeter = useCallback((expectedJobNumber) => {
    const m = meterRef.current;
    if (!m) return;
    if (
      expectedJobNumber !== undefined &&
      expectedJobNumber !== null &&
      String(expectedJobNumber).trim() !== '' &&
      String(m.jobNumber).trim() !== String(expectedJobNumber).trim()
    ) {
      console.warn(
        'SoftMeter.reEnableMeter: expected trip',
        expectedJobNumber,
        'but meter is for',
        m.jobNumber,
        '— ignoring'
      );
      return;
    }
    m.enable();
    setIsRunning(true);
    setFare(m.fare);
    setFareMode(m.fareMode);
    setDistance(m.totalDistance);
    setElapsedTime(m.elapsedSeconds);
    setFareTimeSeconds(m.fareTimeSeconds);
    setWaitTime(m.waitTimeSeconds);
    setWaitCharge(Math.round((m.waitCharge || 0) * 100) / 100);
    setDistanceCharge(Math.round((m.distanceCharge || 0) * 100) / 100);
    setTimeCharge(Math.round((m.timeCharge || 0) * 100) / 100);
    startDisplayTimer();
    startFlushLoop();
  }, [startDisplayTimer, startFlushLoop]);

  const onLocationUpdate = useCallback((lat, lng, speedMs, timestamp) => {
    if (meterRef.current && meterRef.current.enabled) {
      // eslint-disable-next-line no-console
      console.log('[METER] useSoftMeter → meter.onLocationUpdate', {
        lat, lng, speedMs, timestamp,
        enabled: meterRef.current.enabled,
        gapPending: meterRef.current._gapPending,
        meterJob: meterRef.current.jobNumber,
        meterLastFixTs: meterRef.current._lastFixTimestamp,
        traceBufLen: meterRef.current.getTraceBuffer().length,
      });
      meterRef.current.onLocationUpdate(lat, lng, speedMs, timestamp);
    } else {
      // eslint-disable-next-line no-console
      console.log('[METER] useSoftMeter dropping fix — meter not enabled', {
        lat, lng, hasMeter: !!meterRef.current, enabled: meterRef.current?.enabled,
      });
    }
  }, []);

  const calculateFinalFare = useCallback(() => {
    if (!meterRef.current) return 0;
    return meterRef.current.calculateFare(true);
  }, []);

  const getFareBreakdown = useCallback(() => {
    if (!meterRef.current) return null;
    return meterRef.current.getFareBreakdown(true);
  }, []);

  const getMeterStats = useCallback(() => {
    if (!meterRef.current) return null;
    return meterRef.current.getMeterStats();
  }, []);

  /** Currently-running trip number, or '' when idle. Used for cross-trip safety checks. */
  const getRunningJobNumber = useCallback(() => {
    return meterRef.current?.enabled ? String(meterRef.current.jobNumber || '').trim() : '';
  }, []);

  // ── visibilitychange: snapshot pre-gap point + flush trace post-resume ───
  useEffect(() => {
    const onVisibility = async () => {
      const m = meterRef.current;
      // eslint-disable-next-line no-console
      console.log('[METER] visibilitychange', {
        state: typeof document !== 'undefined' ? document.visibilityState : 'n/a',
        hasMeter: !!m,
        enabled: m?.enabled,
      });
      if (!m || !m.enabled) return;
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'hidden') {
        m.onPause();
      } else if (document.visibilityState === 'visible') {
        // Try to recover any gap with OSRM Route between last-known and the next post-resume fix.
        const anchor = m.consumeGapAnchor();
        if (anchor) {
          runGapRecovery(anchor);
        }
        flushTrace();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [flushTrace, runGapRecovery]);

  // Recover persisted meter state on mount
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[METER] useSoftMeter mount-effect SETUP running');
    const saved = SoftMeter.getPersistedState();
    // eslint-disable-next-line no-console
    console.log('[METER] useSoftMeter persisted-state lookup', {
      found: !!saved,
      hasConfig: !!saved?.config,
      jobNumber: saved?.jobNumber,
      totalJobDistance: saved?.totalJobDistance,
      fareTime: saved?.fareTime,
    });
    if (saved && saved.config) {
      resumeFromPersisted(saved);
    }

    return () => {
      // eslint-disable-next-line no-console
      console.log('[METER] useSoftMeter mount-effect CLEANUP firing (hook unmounting)', {
        hadMeter: !!meterRef.current,
        meterJobNumber: meterRef.current?.jobNumber,
      });
      // CRITICAL: do NOT call disable() here — it clears persisted state and would
      // wipe an in-progress flag-trip's accumulated totals on a transient unmount
      // (background suspend, hot-reload, parent re-render). Use detachInMemory()
      // which stops timers and persists once more without touching storage.
      if (meterRef.current) {
        try { meterRef.current.detachInMemory(); } catch (e) { /* ignore */ }
        meterRef.current = null;
      }
      stopDisplayTimer();
      stopFlushLoop();
    };
  // Only run on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    fare,
    distance,
    fareMode,
    isRunning,
    meterConfig,
    elapsedTime,
    fareTimeSeconds,
    waitTime,
    waitCharge,
    distanceCharge,
    timeCharge,
    startMeter,
    stopMeter,
    pauseMeter,
    reEnableMeter,
    onLocationUpdate,
    calculateFinalFare,
    getFareBreakdown,
    getMeterStats,
    getRunningJobNumber,
  };
}
